/**
 * IndicatorTitle.jsx
 * Title component for indicators with icon and outlook label
 * Migration from front/src/components/IndicatorTitle.js to React 19 + Modern architecture
 */
import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { indicatorsConfiguration } from '../config/settings';
import OutlookLabel from './OutlookLabel';

/**
 * Indicator title component with icon and outlook label
 * @param {Object} props - Component props
 * @param {string} props.id - Indicator identifier for navigation anchor
 * @param {React.ReactNode} props.children - Title content
 * @param {Object} props.sx - Additional styling
 * @param {Object} props...rest - Additional props passed to Typography
 * @returns {React.ReactElement} IndicatorTitle component
 */
const IndicatorTitle = ({ id, children, sx = {}, ...rest }) => (
  <Typography
    variant="h4"
    id={id}
    sx={{
      marginTop: '-50px',
      paddingTop: '70px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap-reverse',
      '@media print': {
        paddingTop: '5px',
        marginTop: 0,
        fontSize: '1.2em',
      },
      ...sx,
    }}
    gutterBottom
    {...rest}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: {
          xs: 'reset',
          sm: '1 1 0%',
        },
        pr: 4,
      }}
    >
      {indicatorsConfiguration[id]?.iconName && (
        <Box
          component="a"
          href={`#${id}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            textDecoration: 'none',
            pr: 1,
          }}
          aria-label={`Lien direct vers l'indicateur ${id}`}
        >
          <Box
            component="img"
            sx={{ height: '1.2em' }}
            alt=""
            src={`/pictogrammes/${indicatorsConfiguration[id]?.iconName}`}
          />
        </Box>
      )}

      {children}
    </Box>

    <OutlookLabel
      sx={{
        marginLeft: 'auto',
        my: 1,
        fontSize: {
          default: '0.75em',
          sm: '1em',
        },
      }}
    />
  </Typography>
);

IndicatorTitle.displayName = 'IndicatorTitle';

export default memo(IndicatorTitle);
